import { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { filter } from 'lodash';
// @mui
import { styled, useTheme } from '@mui/material/styles';
import {
  Stack,
  Typography,
  Grid,
  Container,
  Card,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Button,
  Box,
  Divider,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  Paper,
  TableRow,
  Menu,
  MenuItem,
  Alert,
  useMediaQuery,
  OutlinedInput,
  InputAdornment,
} from '@mui/material';
// components
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import Iconify from '../components/iconify';
import PdfComp from '../components/pdf-reader/index';
import Label from '../components/label';
import Scrollbar from '../components/scrollbar';
import { WebhookDeliveriesPanel } from '../components/webhook-deliveries';
// sections
import { EnvelopeListHead, EnvelopeListToolbar } from '../sections/@dashboard/envelope';
// context
import { EntityContext } from '../entity';
import { isEnvelopeOrBatchCancelable } from '../utils/envelopeAndBatchCancelable';
import { waitBatchZipUrl } from '../utils/batchZipDownload';
import { BATCH_PROGRESS_POLL_MS, batchProgressLines, batchStatusLabel, isBatchTerminal } from '../utils/batchProgress';

// ----------------------------------------------------------------------

const APP_BAR_MOBILE = 64;
const APP_BAR_DESKTOP = 92;

const StyledRoot = styled('div')({
  display: 'flex',
  minHeight: '100%',
  overflow: 'hidden',
});

const Main = styled('div')(({ theme }) => ({
  flexGrow: 1,
  overflow: 'auto',
  minHeight: '100%',
  paddingTop: APP_BAR_MOBILE + 8,
  paddingBottom: theme.spacing(10),
  [theme.breakpoints.up('lg')]: {
    paddingTop: APP_BAR_DESKTOP + 8,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
}));

// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: 'name', label: 'Nome', alignRight: false },
  { id: 'signersQuantity', label: 'Quantidade de Assinantes', alignRight: false },
  { id: 'signersMinimum', label: 'Minimo de Assinaturas', alignRight: false },
  { id: 'done', label: 'Finalizado', alignRight: false },
  { id: '' },
];

// Fases sem contagem (creating, running, created) ou lote em voo sem total_count
const TEXTO_DA_FASE = {
  created: 'Lote aberto, aguardando o fechamento.',
  creating: 'Preparando os envelopes do lote.',
  running: 'Aguardando as assinaturas.',
  canceled: 'Lote cancelado.',
};

// ----------------------------------------------------------------------

function descendingComparator(a, b, orderBy) {
  // Mapear os IDs das colunas para os campos reais dos dados
  const fieldMap = {
    'name': 'group_name',
    'signersQuantity': 'number_of_signers',
    'signersMinimum': 'signers_minimum',
    'done': 'done',
  };
  
  const actualField = fieldMap[orderBy] || orderBy;
  let aValue = a[actualField];
  let bValue = b[actualField];
  
  // Tratar valores nulos ou indefinidos
  if (aValue == null) aValue = '';
  if (bValue == null) bValue = '';
  
  // Para campos numéricos, comparar como números
  if (actualField === 'number_of_signers' || actualField === 'signers_minimum') {
    const aNum = Number(aValue);
    const bNum = Number(bValue);
    
    if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) {
      if (bNum < aNum) {
        return -1;
      }
      if (bNum > aNum) {
        return 1;
      }
      return 0;
    }
  }
  
  // Para campos booleanos (done), tratar como boolean
  if (actualField === 'done') {
    const aBool = Boolean(aValue);
    const bBool = Boolean(bValue);
    
    if (bBool && !aBool) {
      return -1;
    }
    if (aBool && !bBool) {
      return 1;
    }
    return 0;
  }
  
  // Para outros campos (strings), comparar como strings (case-insensitive)
  const aStr = String(aValue).toLowerCase();
  const bStr = String(bValue).toLowerCase();
  
  if (bStr < aStr) {
    return -1;
  }
  if (bStr > aStr) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query, fieldName = 'group_name') {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  const sortedArray = stabilizedThis.map((el) => el[0]);
  if (query) {
    return filter(sortedArray, (_item) => {
      const fieldValue = _item[fieldName];
      if (fieldValue == null) return false;
      const fieldValueStr = String(fieldValue);
      const queryStr = String(query);
      return fieldValueStr.toLowerCase().indexOf(queryStr.toLowerCase()) !== -1;
    });
  }
  return sortedArray;
}

// ----------------------------------------------------------------------

export default function BatchEndorsementViewPage() {
  const { batchEndorsementKey } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [batchEndorsement, setBatchEndorsement] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [groupsList, setGroupsList] = useState([]);
  const [envelopeUrls, setEnvelopeUrls] = useState({});

  // Estados para a tabela de grupos
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState('name');
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // Paginação da lista de envelopes do lote
  const [envelopePage, setEnvelopePage] = useState(0);
  const [envelopesPerPage, setEnvelopesPerPage] = useState(500);
  const [totalEnvelopesAll, setTotalEnvelopesAll] = useState(0);
  const [envelopeFilterName, setEnvelopeFilterName] = useState('');

  // Estados para download ZIP
  const [fileType, setFileType] = useState('original');
  const [isDownloading, setIsDownloading] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState(null);
  const [isCancelingBatch, setIsCancelingBatch] = useState(false);
  const unmountedRef = useRef(false);

  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
    };
  }, []);

  const {
    getBatchEndorsementDetails,
    getEnvelope,
    finalizeBatchEndorsement,
    downloadBatchEndorsementZip,
    cancelBatchEndorsement,
  } = useContext(EntityContext);

  const loadBatchEndorsement = async (page = 0, perPage = envelopesPerPage) => {
    if (!batchEndorsementKey) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const data = await getBatchEndorsementDetails(batchEndorsementKey, {
        limit: perPage,
        offset: page * perPage,
      });

      if (data && typeof data === 'object') {
        setBatchEndorsement(data);
        setTotalEnvelopesAll(data.total_envelopes_all ?? data.total_envelopes ?? 0);
        setEnvelopePage(page);
        setSelectedDocument(null);
        setGroupsList([]);
      } else {
        setBatchEndorsement(null);
        setTotalEnvelopesAll(0);
      }
    } catch (error) {
      console.error('Error loading batch endorsement:', error);
      setBatchEndorsement(null);
      setTotalEnvelopesAll(0);
    } finally {
      setLoading(false);
    }
  };

  const loadGroupsForEnvelope = async (envelopeKey) => {
    if (!envelopeKey) return;
    
    try {
      const envelopeData = await getEnvelope(envelopeKey);
      if (envelopeData && envelopeData.groups) {
        setGroupsList(envelopeData.groups);
      } else {
        setGroupsList([]);
      }
    } catch (error) {
      console.error('Error loading envelope groups:', error);
      setGroupsList([]);
    }
  };

  // CHAN-122: a tela abre pelo resumo (sem envelopes nem grupos); a lista só carrega em finished
  const loadSummary = async () => {
    const data = await getBatchEndorsementDetails(batchEndorsementKey, { summary: true });
    if (unmountedRef.current || !data || typeof data !== 'object') return null;
    setBatchEndorsement(data);
    return data;
  };

  useEffect(() => {
    if (!batchEndorsementKey) {
      setLoading(false);
      return;
    }
    setEnvelopePage(0);
    (async () => {
      try {
        setLoading(true);
        const resumo = await loadSummary();
        if (resumo?.status === 'finished') await loadBatchEndorsement(0, envelopesPerPage);
      } catch (error) {
        console.error('Error loading batch endorsement:', error);
        setBatchEndorsement(null);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload only when batch key changes
  }, [batchEndorsementKey]);

  // Relê o resumo enquanto o lote não termina; para em finished/canceled ou ao sair da página
  const batchStatus = batchEndorsement?.status;
  useEffect(() => {
    if (!batchStatus || isBatchTerminal(batchStatus)) return undefined;
    const timer = setInterval(async () => {
      try {
        const resumo = await loadSummary();
        if (resumo?.status === 'finished') loadBatchEndorsement(0, envelopesPerPage);
      } catch (error) {
        console.error('Error refreshing batch summary:', error);
      }
    }, BATCH_PROGRESS_POLL_MS);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- restart only when the batch status changes
  }, [batchStatus]);

  const handleDocumentSelect = async (document) => {
    setSelectedDocument(document);
    
    // Carregar grupos do envelope selecionado
    await loadGroupsForEnvelope(document.envelope_key);
    
    // Não buscar URL do PDF se o documento ainda está em criação (PDF não existe)
    if (document.status === 'created' || document.status === 'creating') {
      return;
    }
    // Se não temos a URL ainda, buscar
    if (!envelopeUrls[document.envelope_key]) {
      try {
        const envelopeData = await getEnvelope(document.envelope_key);
        setEnvelopeUrls(prev => ({
          ...prev,
          [document.envelope_key]: {
            url: envelopeData.envelope.url,
            signed_url: envelopeData.envelope.signed_envelope_url
          }
        }));
      } catch (error) {
        console.error('Error loading envelope URL:', error);
      }
    }
  };

  const handleBack = () => {
    navigate('/dashboard/endorsements');
  };

  const handleFinalize = async () => {
    if (!batchEndorsementKey) return;
    
    try {
      await finalizeBatchEndorsement(batchEndorsementKey);
      // Recarregar o resumo do lote para atualizar o status
      await loadSummary();
    } catch (error) {
      console.error('Error finalizing batch endorsement:', error);
    }
  };

  const handleCancelBatch = async () => {
    if (!batchEndorsementKey) return;
    try {
      setIsCancelingBatch(true);
      await cancelBatchEndorsement(batchEndorsementKey);
      navigate('/dashboard/endorsements');
    } catch (error) {
      console.error('Error canceling batch endorsement:', error);
      setNotification({
        open: true,
        message:
          error.response?.data?.detail ||
          'Não foi possível cancelar o lote. Tente novamente.',
        severity: 'error',
      });
    } finally {
      setIsCancelingBatch(false);
    }
  };

  const handleDownloadZip = async (selectedFileType = null) => {
    if (!batchEndorsementKey) return;
    
    // Fechar menu se estiver aberto
    setDownloadMenuAnchor(null);
    
    try {
      setIsDownloading(true);
      
      // Determinar file_type baseado no status
      let fileTypeToSend = 'original';
      if (batchEndorsement.status === 'finished') {
        fileTypeToSend = selectedFileType || fileType; // Usar valor selecionado ou padrão
      }
      
      const downloadUrl = await waitBatchZipUrl(
        () => downloadBatchEndorsementZip(batchEndorsementKey, fileTypeToSend),
        {
          isCanceled: () => unmountedRef.current,
          onWaiting: () => setNotification({
            open: true,
            message: 'Gerando o ZIP do lote. O download começa sozinho quando ficar pronto, pode levar alguns minutos.',
            severity: 'info'
          }),
        }
      );
      if (unmountedRef.current) return;

      if (downloadUrl) {
        setNotification({ open: false, message: '', severity: 'info' });
        // Mesma aba: o S3 entrega o ZIP como download e a página continua aberta.
        // Aba nova aberta minutos depois do clique seria barrada pelo bloqueador de pop-up.
        window.location.assign(downloadUrl);
      } else {
        setNotification({
          open: true,
          message: 'O ZIP ainda está sendo gerado. Você receberá um email quando estiver pronto.',
          severity: 'info'
        });
      }
    } catch (error) {
      console.error('Error downloading ZIP:', error);
      setNotification({
        open: true,
        message: 'Erro ao processar download. Tente novamente mais tarde.',
        severity: 'error'
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDownloadMenuOpen = (event) => {
    setDownloadMenuAnchor(event.currentTarget);
  };

  const handleDownloadMenuClose = () => {
    setDownloadMenuAnchor(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'finished':
        return 'success';
      case 'processing':
      case 'running':
        return 'warning';
      case 'canceled':
        return 'error';
      case 'pending':
        return 'info';
      case 'created':
        return 'warning';
      case 'creating':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const downloadFile = (filePath, fileName) => {
    // Open direct so the browser shows progress immediately (large files).
    // Note: filename will be determined by server headers (Content-Disposition) / URL.
    const link = document.createElement('a');
    link.href = filePath;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
  };

  const handleDownload = async (envelope) => {
    const filename = envelope.envelope_name;
    const finalFilename = `${filename}.pdf`;
    
    // Se não temos a URL ainda, buscar
    if (!envelopeUrls[envelope.envelope_key]) {
      try {
        const envelopeData = await getEnvelope(envelope.envelope_key);
        setEnvelopeUrls(prev => ({
          ...prev,
          [envelope.envelope_key]: {
            url: envelopeData.envelope.url,
            signed_url: envelopeData.envelope.signed_envelope_url
          }
        }));
        
        // Usar a URL recém carregada
        const filePath = envelope.status === 'finished' && envelopeData.envelope.signed_envelope_url 
          ? envelopeData.envelope.signed_envelope_url 
          : envelopeData.envelope.url;
        
        downloadFile(filePath, finalFilename);
      } catch (error) {
        console.error('Error loading envelope URL for download:', error);
      }
    } else {
      // Usar URL do cache
      const filePath = envelope.status === 'finished' && envelopeUrls[envelope.envelope_key].signed_url 
        ? envelopeUrls[envelope.envelope_key].signed_url 
        : envelopeUrls[envelope.envelope_key].url;
      
      downloadFile(filePath, finalFilename);
    }
  };

  // Handlers para a tabela de grupos
  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = groupsList.map((n) => n.group_name);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, name) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
    }
    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setPage(0);
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  const handleFilterByName = (event) => {
    setPage(0);
    setFilterName(event.target.value);
  };

  const handleViewGroup = (groupKey) => {
    navigate(`/dashboard/envelope/group/${groupKey}`);
  };

  const filteredGroups = applySortFilter(groupsList, getComparator(order, orderBy), filterName, 'group_name');
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - filteredGroups.length) : 0;
  const isNotFound = !filteredGroups.length && !!filterName;

  const envelopeFilterQuery = envelopeFilterName.trim().toLowerCase();
  const filteredEnvelopes = (batchEndorsement?.envelopes || []).filter((envelope) => {
    if (!envelopeFilterQuery) return true;
    return String(envelope.envelope_name || '').toLowerCase().includes(envelopeFilterQuery);
  });

  if (loading) {
    return (
      <StyledRoot>
        <Main>
          <Container maxWidth="xl">
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
              <CircularProgress />
            </Box>
          </Container>
        </Main>
      </StyledRoot>
    );
  }

  if (!batchEndorsement) {
    return (
      <StyledRoot>
        <Main>
          <Container maxWidth="xl">
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
              <Typography variant="h6">Lote não encontrado</Typography>
            </Box>
          </Container>
        </Main>
      </StyledRoot>
    );
  }

  const mostraLista = batchEndorsement.status === 'finished';
  const totalDoLote = mostraLista ? totalEnvelopesAll : batchEndorsement.total_count;
  const linhasDeProgresso = batchProgressLines(batchEndorsement);

  return (
    <StyledRoot>
      <Main>
        <Helmet>
          <title>Visualizar Lote - {batchEndorsement.batch_envelope_name}</title>
        </Helmet>

        <Container maxWidth="xl">
          {/* Header */}
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            alignItems={{ xs: 'flex-start', sm: 'center' }} 
            justifyContent="space-between" 
            spacing={{ xs: 2, sm: 0 }}
            mb={2}
          >
            <Stack direction="row" alignItems="center" spacing={2} sx={{ flex: 1, minWidth: 0 }}>
              <IconButton onClick={handleBack} color="inherit">
                <Iconify icon="eva:arrow-back-fill" />
              </IconButton>
              <Typography 
                variant="h4" 
                sx={{ 
                  fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2.125rem' },
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1
                }}
              >
                {batchEndorsement.batch_envelope_name}
              </Typography>
            </Stack>
            <Stack 
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'flex-start', sm: 'center' }} 
              spacing={{ xs: 1.5, sm: 2 }}
              sx={{ 
                width: { xs: '100%', sm: 'auto' },
                flexWrap: 'wrap'
              }}
            >
              {batchEndorsement.status === 'created' && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleFinalize}
                  startIcon={<Iconify icon="eva:checkmark-circle-2-fill" />}
                  fullWidth={isMobile}
                  size={isMobile ? 'medium' : 'medium'}
                >
                  Fechar Lote
                </Button>
              )}
              {isEnvelopeOrBatchCancelable(batchEndorsement.status) && (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleCancelBatch}
                  disabled={isCancelingBatch}
                  startIcon={
                    isCancelingBatch ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : (
                      <Iconify icon="eva:trash-2-outline" />
                    )
                  }
                  fullWidth={isMobile}
                >
                  {isCancelingBatch ? 'Cancelando...' : 'Cancelar'}
                </Button>
              )}
              {batchEndorsement.status !== 'created' && (
                <>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={batchEndorsement.status === 'finished' ? handleDownloadMenuOpen : () => handleDownloadZip()}
                    disabled={isDownloading}
                    endIcon={batchEndorsement.status === 'finished' ? <Iconify icon={downloadMenuAnchor ? 'eva:chevron-up-fill' : 'eva:chevron-down-fill'} /> : null}
                    startIcon={isDownloading ? <CircularProgress size={20} /> : <DownloadIcon />}
                    fullWidth={isMobile}
                    size={isMobile ? 'medium' : 'medium'}
                  >
                    {isDownloading ? 'Processando...' : 'Download ZIP'}
                  </Button>
                  {batchEndorsement.status === 'finished' && (
                    <Menu
                      anchorEl={downloadMenuAnchor}
                      open={Boolean(downloadMenuAnchor)}
                      onClose={handleDownloadMenuClose}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    >
                      <MenuItem onClick={() => handleDownloadZip('original')}>
                        Original
                      </MenuItem>
                      <MenuItem onClick={() => handleDownloadZip('signed')}>
                        Assinado
                      </MenuItem>
                      <MenuItem onClick={() => handleDownloadZip('both')}>
                        Ambos
                      </MenuItem>
                    </Menu>
                  )}
                </>
              )}
              <Stack 
                direction="row" 
                alignItems="center" 
                spacing={1.5}
                sx={{ width: { xs: '100%', sm: 'auto' }, justifyContent: { xs: 'space-between', sm: 'flex-start' } }}
              >
                <Label color={getStatusColor(batchEndorsement.status)}>
                  {batchStatusLabel(batchEndorsement.status)}
                </Label>
                {totalDoLote != null && (
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    {totalDoLote} envelopes
                  </Typography>
                )}
              </Stack>
            </Stack>
          </Stack>

          {/* Notificação */}
          {notification.open && (
            <Alert 
              severity={notification.severity} 
              onClose={() => setNotification({ ...notification, open: false })}
              sx={{ mb: 2 }}
            >
              {notification.message}
            </Alert>
          )}

          {/* Informações do lote */}
          <Card sx={{ mb: 2, p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Data de criação
                </Typography>
                <Typography variant="body1">
                  {batchEndorsement.created_at ? 
                    batchEndorsement.created_at.substring(0, 10).concat(' ').concat(batchEndorsement.created_at.substring(11, 19)) 
                    : 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Última alteração
                </Typography>
                <Typography variant="body1">
                  {batchEndorsement.updated_at ? 
                    batchEndorsement.updated_at.substring(0, 10).concat(' ').concat(batchEndorsement.updated_at.substring(11, 19)) 
                    : 'N/A'}
                </Typography>
              </Grid>
            </Grid>
          </Card>

          {/* Lote em andamento: estado e contagem agregada, sem status por envelope (CHAN-122) */}
          {!mostraLista && (
            <Card sx={{ p: 3 }}>
              <Stack spacing={1}>
                <Typography variant="h6">Andamento do lote</Typography>
                {linhasDeProgresso.length > 0 ? (
                  linhasDeProgresso.map((linha) => (
                    <Typography key={linha} variant="body1">{linha}</Typography>
                  ))
                ) : (
                  <Typography variant="body1">{TEXTO_DA_FASE[batchEndorsement.status] ?? 'Processando os documentos.'}</Typography>
                )}
                {!isBatchTerminal(batchEndorsement.status) && (
                  <Typography variant="caption" color="text.secondary">
                    {linhasDeProgresso.length > 0 ? 'Contagem aproximada, atualizada' : 'Atualizado'} a cada 15 segundos.
                    {' '}A lista de envelopes aparece quando o lote for finalizado.
                  </Typography>
                )}
              </Stack>
            </Card>
          )}

          {/* Conteúdo principal */}
          {mostraLista && (
          <Grid container spacing={3}>
            {/* Visualizador de PDF */}
            <Grid item xs={12} lg={8}>
              <Card sx={{ height: '70vh', overflow: 'hidden' }}>
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                  <Typography variant="h6">
                    {selectedDocument ? selectedDocument.envelope_name : 'Selecione um envelope'}
                  </Typography>
                </Box>
                <Box sx={{ height: 'calc(100% - 80px)', overflow: 'auto' }}>
                  {selectedDocument ? (
                    (batchEndorsement.status === 'created' || selectedDocument.status === 'created' || selectedDocument.status === 'creating') ? (
                      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                        <Typography variant="body1" color="text.secondary">
                          Visualização ainda não disponível
                        </Typography>
                      </Box>
                    ) : envelopeUrls[selectedDocument?.envelope_key]?.url ? (
                      <PdfComp
                        pdfFile={
                          batchEndorsement.status === 'finished' && envelopeUrls[selectedDocument?.envelope_key]?.signed_url
                            ? envelopeUrls[selectedDocument?.envelope_key]?.signed_url
                            : envelopeUrls[selectedDocument?.envelope_key]?.url
                        }
                        enableSignaturePlacement={false}
                      />
                    ) : (
                      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                        <CircularProgress />
                        <Typography variant="body1" color="text.secondary" sx={{ ml: 2 }}>
                          Carregando documento...
                        </Typography>
                      </Box>
                    )
                  ) : (
                    <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                      <Typography variant="body1" color="text.secondary">
                        Clique em algum registro para mostrar o conteúdo
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Card>
            </Grid>

            {/* Lista de documentos */}
            <Grid item xs={12} lg={4}>
              <Card sx={{ height: '70vh', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                  <Typography variant="h6" sx={{ mb: 1.5 }}>
                    Envelopes do Lote
                  </Typography>
                  <OutlinedInput
                    fullWidth
                    size="small"
                    value={envelopeFilterName}
                    onChange={(e) => setEnvelopeFilterName(e.target.value)}
                    placeholder="Buscar contrato nesta página..."
                    startAdornment={
                      <InputAdornment position="start">
                        <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled', width: 20, height: 20 }} />
                      </InputAdornment>
                    }
                  />
                </Box>
                <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
                  <List>
                    {filteredEnvelopes.length === 0 && envelopeFilterQuery ? (
                      <Box sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          Nenhum envelope encontrado nesta página para &quot;{envelopeFilterName.trim()}&quot;.
                        </Typography>
                      </Box>
                    ) : null}
                    {filteredEnvelopes.map((envelope, index) => (
                      <div key={envelope.envelope_key || index}>
                        <ListItem 
                          button 
                          selected={selectedDocument?.envelope_key === envelope.envelope_key}
                          onClick={() => handleDocumentSelect(envelope)}
                          sx={{
                            '&.Mui-selected': {
                              backgroundColor: 'primary.lighter',
                            },
                          }}
                        >
                          <ListItemIcon>
                            <Iconify icon="eva:file-text-fill" />
                          </ListItemIcon>
                          <ListItemText
                            primary={envelope.envelope_name}
                            secondary={
                              <Stack direction="row" alignItems="center" spacing={1} mt={0.5}>
                                <Chip 
                                  label={envelope.status} 
                                  size="small" 
                                  color={getStatusColor(envelope.status)}
                                />
                                <Typography variant="caption" color="text.secondary">
                                  {envelope.created_at ? 
                                    envelope.created_at.substring(0, 10) : 'N/A'}
                                </Typography>
                              </Stack>
                            }
                          />
                          {(envelope.status === 'running' || envelope.status === 'finished') && (
                            <IconButton 
                              size="small" 
                              color="inherit" 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDownload(envelope);
                              }}
                              sx={{ ml: 1 }}
                            >
                              <DownloadIcon />
                            </IconButton>
                          )}
                        </ListItem>
                        {index < filteredEnvelopes.length - 1 && <Divider />}
                      </div>
                    ))}
                  </List>
                </Box>
                <TablePagination
                  component="div"
                  count={totalEnvelopesAll}
                  page={envelopePage}
                  onPageChange={(_, newPage) => {
                    loadBatchEndorsement(newPage, envelopesPerPage);
                  }}
                  rowsPerPage={envelopesPerPage}
                  onRowsPerPageChange={(e) => {
                    const next = parseInt(e.target.value, 10);
                    setEnvelopesPerPage(next);
                    loadBatchEndorsement(0, next);
                  }}
                  rowsPerPageOptions={[50, 100, 500]}
                  labelRowsPerPage="Por pág."
                  labelDisplayedRows={({ from, to, count }) =>
                    (count !== -1 ? `${from}–${to} / ${count}` : `${from}–${to}`)
                  }
                  sx={{
                    overflow: 'hidden',
                    borderTop: 1,
                    borderColor: 'divider',
                    '& .MuiTablePagination-toolbar': {
                      flexWrap: 'wrap',
                      justifyContent: 'center',
                      gap: 0.25,
                      px: 0.5,
                      minHeight: 40,
                    },
                    '& .MuiTablePagination-spacer': { display: 'none' },
                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select': {
                      fontSize: '0.7rem',
                      m: 0,
                    },
                    '& .MuiTablePagination-actions': { ml: 0 },
                    '& .MuiIconButton-root': { p: 0.5 },
                  }}
                />
              </Card>
            </Grid>
          </Grid>
          )}

          {/* Tabela de Grupos de Signatários */}
          {selectedDocument && (
            <Card sx={{ mt: 3 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2} sx={{ p: 3 }}>
                <Typography variant="h5">
                  Grupos de Assinantes
                </Typography>
              </Stack>
              
              <EnvelopeListToolbar numSelected={selected.length} filterName={filterName} onFilterName={handleFilterByName} />

              <Scrollbar>
                <TableContainer sx={{ minWidth: 800 }}>
                  <Table>
                    <EnvelopeListHead
                      order={order}
                      orderBy={orderBy}
                      headLabel={TABLE_HEAD}
                      rowCount={filteredGroups.length}
                      numSelected={selected.length}
                      onRequestSort={handleRequestSort}
                      onSelectAllClick={handleSelectAllClick}
                    />
                    <TableBody>
                      {filteredGroups.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => {
                        return (
                          <TableRow hover key={row.group_key} tabIndex={-1}>
                            <TableCell component="th" scope="row" padding="normal">
                              <Stack direction="row" alignItems="center" spacing={2}>
                                <Typography variant="subtitle2" noWrap>
                                  {row.group_name}
                                </Typography>
                              </Stack>
                            </TableCell>

                            <TableCell align="left">{row.number_of_signers}</TableCell>
                            <TableCell align="left">{row.signers_minimum}</TableCell>
                            <TableCell align="left">{row.done ? 'Sim' : 'Não'}</TableCell>

                            <TableCell align="right">
                              <IconButton size="small" color="inherit" onClick={() => handleViewGroup(row.group_key)}>
                                <VisibilityIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {emptyRows > 0 && (
                        <TableRow style={{ height: 53 * emptyRows }}>
                          <TableCell colSpan={5} />
                        </TableRow>
                      )}
                    </TableBody>

                    {isNotFound && (
                      <TableBody>
                        <TableRow>
                          <TableCell align="center" colSpan={5} sx={{ py: 3 }}>
                            <Paper
                              sx={{
                                textAlign: 'center',
                              }}
                            >
                              <Typography variant="h6" paragraph>
                                Nenhum grupo encontrado
                              </Typography>

                              <Typography variant="body2">
                                Nenhum resultado encontrado para &nbsp;
                                <strong>&quot;{filterName}&quot;</strong>.
                                <br /> Verifique a ortografia ou use palavras completas.
                              </Typography>
                            </Paper>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    )}
                  </Table>
                </TableContainer>
              </Scrollbar>

              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={filteredGroups.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Card>
          )}

          {/* CHAN-70: histórico de webhooks do lote */}
          <Card sx={{ mt: 3 }}>
            <Typography variant="h5" sx={{ px: 3, pt: 3 }}>
              Webhooks
            </Typography>
            <WebhookDeliveriesPanel batchEnvelopeKey={batchEndorsementKey} />
          </Card>
        </Container>
      </Main>
    </StyledRoot>
  );
} 